/**
 * Fantasy Points Calculation Utilities
 *
 * Converts raw positions and stats to fantasy points based on F1 Fantasy rules.
 * Reference: scraper-utils/output/2025-f1fantasy-rules.md
 */

import { DEFAULT_SEASON } from "./constants";
import { getScoringRules } from "./scoring-rules";

// Qualifying points: P1=10, P2=9, P3=8, P4=7, P5=6, P6=5, P7=4, P8=3, P9=2, P10=1, P11-20=0
// Note: Drivers who do not set a time in Q1 (null position) get -5 points penalty
export const NO_QUALIFYING_TIME_PENALTY = -5;

export function getQualifyingPoints(
  position: number | null | undefined,
): number {
  // No qualifying time set = -5 points penalty (per F1 Fantasy rules Note 1)
  if (position === null || position === undefined)
    return NO_QUALIFYING_TIME_PENALTY;
  if (position < 1) return 0;
  if (position > 10) return 0;
  return 11 - position; // P1=10, P2=9, etc.
}

// Race points: P1=25, P2=18, P3=15, P4=12, P5=10, P6=8, P7=6, P8=4, P9=2, P10=1, P11-20=0
const RACE_POINTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
export function getRacePoints(position: number | null | undefined): number {
  if (!position || position < 1) return 0;
  if (position > 10) return 0;
  return RACE_POINTS[position - 1];
}

// Sprint points: P1=8, P2=7, P3=6, P4=5, P5=4, P6=3, P7=2, P8=1, P9-20=0
export function getSprintPoints(position: number | null | undefined): number {
  if (!position || position < 1) return 0;
  if (position > 8) return 0;
  return 9 - position; // P1=8, P2=7, etc.
}

// DNF penalty: -20 points for race, -10 for sprint (confirmed via China 2026 sprint data)
export const DNF_PENALTY = -20;
export const SPRINT_DNF_PENALTY = -10;

// Fastest lap bonus: 10 points (race), 5 points (sprint)
export const FASTEST_LAP_RACE = 10;
export const FASTEST_LAP_SPRINT = 5;

// Driver of the Day bonus: 10 points
export const DRIVER_OF_THE_DAY = 10;

// Positions gained/lost: 1 point per position
export function getPositionsGainedPoints(positionsGained: number): number {
  return positionsGained; // 1 point per position gained, -1 per position lost
}

// Overtakes: 1 point per overtake
export function getOvertakesPoints(overtakes: number): number {
  return overtakes;
}

/**
 * Calculate all fantasy points for a driver in a race
 */
export interface DriverFantasyBreakdown {
  // Qualifying
  qualifyingPoints: number;
  // Sprint (if applicable)
  sprintPositionPoints: number;
  sprintPositionsGainedPoints: number;
  sprintOvertakesPoints: number;
  sprintFastestLapPoints: number;
  sprintDnfPenalty: number;
  sprintTotal: number;
  // Race
  racePositionPoints: number;
  racePositionsGainedPoints: number;
  raceOvertakesPoints: number;
  raceFastestLapPoints: number;
  raceDriverOfTheDayPoints: number;
  raceDnfPenalty: number;
  raceTotal: number;
  // Grand total
  total: number;
}

export function calculateDriverFantasyBreakdown(
  data: {
    qualifyingPosition: number | null;
    racePosition: number | null;
    raceDnf: boolean;
    positionsGained: number;
    overtakes: number;
    fastestLap: boolean;
    driverOfTheDay: boolean;
    sprintPosition: number | null;
    sprintDnf: boolean;
    sprintPositionsGained: number;
    sprintOvertakes: number;
    sprintFastestLap: boolean;
  },
  season = DEFAULT_SEASON,
): DriverFantasyBreakdown {
  const rules = getScoringRules(season);

  // Qualifying
  const qualifyingPoints = getQualifyingPoints(data.qualifyingPosition);

  // Sprint
  // Note: For DNF drivers:
  // - Position points: 0 (they get DNF penalty instead)
  // - Positions gained: 0 (per rules: "Unclassified drivers will not have any positions lost calculated")
  // - Overtakes: STILL COUNT (driver made overtakes before retiring)
  // - Fastest lap: Can still count if set before retiring
  const sprintPositionPoints = data.sprintDnf
    ? 0
    : getSprintPoints(data.sprintPosition);
  const sprintPositionsGainedPoints = data.sprintDnf
    ? 0
    : getPositionsGainedPoints(data.sprintPositionsGained);
  const sprintOvertakesPoints = getOvertakesPoints(data.sprintOvertakes); // Overtakes count even for DNF
  const sprintFastestLapPoints = data.sprintFastestLap
    ? rules.fastestLapSprint
    : 0;
  const sprintDnfPenalty = data.sprintDnf ? rules.sprintDnfPenalty : 0;
  const sprintTotal =
    sprintPositionPoints +
    sprintPositionsGainedPoints +
    sprintOvertakesPoints +
    sprintFastestLapPoints +
    sprintDnfPenalty;

  // Race
  // Note: For DNF drivers:
  // - Position points: 0 (they get DNF penalty instead)
  // - Positions gained: 0 (per rules: "Unclassified drivers will not have any positions lost calculated")
  // - Overtakes: STILL COUNT (driver made overtakes before retiring)
  // - Fastest lap: Can still count if set before retiring
  const racePositionPoints = data.raceDnf
    ? 0
    : getRacePoints(data.racePosition);
  const racePositionsGainedPoints = data.raceDnf
    ? 0
    : getPositionsGainedPoints(data.positionsGained);
  const raceOvertakesPoints = getOvertakesPoints(data.overtakes); // Overtakes count even for DNF
  const raceFastestLapPoints = data.fastestLap ? rules.fastestLapRace : 0;
  const raceDriverOfTheDayPoints = data.driverOfTheDay
    ? rules.driverOfTheDay
    : 0;
  const raceDnfPenalty = data.raceDnf ? rules.raceDnfPenalty : 0;
  const raceTotal =
    racePositionPoints +
    racePositionsGainedPoints +
    raceOvertakesPoints +
    raceFastestLapPoints +
    raceDriverOfTheDayPoints +
    raceDnfPenalty;

  // Total
  const total = qualifyingPoints + sprintTotal + raceTotal;

  return {
    qualifyingPoints,
    sprintPositionPoints,
    sprintPositionsGainedPoints,
    sprintOvertakesPoints,
    sprintFastestLapPoints,
    sprintDnfPenalty,
    sprintTotal,
    racePositionPoints,
    racePositionsGainedPoints,
    raceOvertakesPoints,
    raceFastestLapPoints,
    raceDriverOfTheDayPoints,
    raceDnfPenalty,
    raceTotal,
    total,
  };
}
